import { Scene } from 'framework/SceneManager/Scene';
import { Game } from 'framework/Game';
import { ScenesName } from 'js/scenes/scenes';

import { BubbleGameBoard } from '../bubbleCore/BubbleGameBoard';
import { BubbleGameBoardView } from '../bubbleCore/view/BubbleGameBoardView';

import { HexGridType } from '../bubbleCore/types';
import { Background } from 'js/scenes/common/Background';
import { ILevelData } from 'js/bubbleCore/levelParser/parseLevel';
import { Controller } from 'js/bubbleCore/Controller';

import { GameOverPopup } from 'js/scenes/ui/GameOverPopup';
import { WaitingScreen } from 'js/scenes/ui/WaitingScreen';

import { loadUserData } from 'js/sdk/loadUserData';
import { saveUserData } from 'js/sdk/saveUserData';
import { logEvent } from 'js/sdk/logEvent';

import triggerGameStart from "js/sdk/triggerGameStart";
import triggerGameOver from "js/sdk/triggerGameOver";
import { appHeight } from 'js/main';
import * as PIXI from 'pixi.js';

let highscore: number;

const levelData: ILevelData = {
  config: {
    fireEvery: 10,
    availableColors: [
      {startSec: 0, colors: 3},
      {startSec: 20, colors: 4},
      {startSec: 50, colors: 5},
      {startSec: 70, colors: 6}
    ]
  },
  grid: {
    type: HexGridType.OddR,
    hexLimits: {x: 0, y: 0, width: 13, height: 30},
    asymetrical: true,
    rowsToFill: 6,
    scrollSpeed: 0.002,
    fillScrollSpeed: 0.01,
    deathLineY: 27,
    scrollFill: 5,
  },
  popOutSpace: {
    gravity: 0.02,
    popOutVelRange: {left: -0.4, right: 0.4, top: -0.2, bottom: 0},
    pointZones: [
      { x: 0, score: 50 },
      { x: 0.2, score: 100 },
      { x: 0.4, score: 250 },
      { x: 0.6, score: 100 },
      { x: 0.8, score: 50 },
    ],
    pointZonesY: 28,
    bouncers: {
      y: 27.05,
      width: 0.02,
      height: 0.05,
    },
  },
  shooter: {
    bubbleSpeed: 1,
    positionY: 30,
    angleLimitDeg: 85,
  },
};

export class MainScene extends Scene {    
  private readonly bg: Background;
  private gameBoard: BubbleGameBoard;
  private gameBoardView: BubbleGameBoardView;
  private controller: Controller;

  private autoUpdate: boolean = true;
  private gameStarted = false;

  private crossPromo: PIXI.Sprite;
  private crossPromoScale: number;
  private crossPromoCount: number;
  private currHeight: number = 0;

  private level: ILevelData;

  constructor(game: Game) {
    super(game);

    // @ts-ignore
    window.scene = this;

    this.bg = this.root.addChild(new Background());
    this.level = JSON.parse(JSON.stringify(levelData));
    this.gameBoard = new BubbleGameBoard();

    this.gameBoardView = new BubbleGameBoardView({
      gameBoard: this.gameBoard,
      hexSize: 27,
      scale: 1.2,
      verticalOffset: 1,
      soundManager: this.game.sounds,
      game: this.game
    });
    this.game.tweens.create(this.gameBoardView.getRoot())
      .from({alpha: 0}, 700, this.game.tweens.easing.Sinusoidal.Out, true);

    this.controller = new Controller(
      this.gameBoard,
      this.gameBoardView,
      {
        onGameOver: async () => await this.gameOver(),
        mobile: window.ontouchstart !== undefined,
      }
    );

    this.root.addChild(this.gameBoardView.getRoot());
    this.loadScore();

    // this.initDebug();
    this.loadCrossPromo();
  }

  private async loadCrossPromo(): Promise<void> {
    let gameData = await window.sdkHandler.getCrossPromoGameData();
    // gameData = {
    // 	url: '',
    // 	iconURL: 'https://avatars.mds.yandex.net/get-games/11385414/2a0000019352d4a7131e20673d95b0e68e93/icon1'
    // };
    if (gameData) {
      this.crossPromo = new PIXI.Sprite(PIXI.Texture.from(gameData.iconURL));
      this.crossPromo.anchor.set(0.5);
      this.crossPromo.interactive = true; // Make the sprite interactive to listen to events
      this.crossPromo.buttonMode = true; // Optionally, set button mode for better UX
      this.crossPromo.on('pointerup', () => {
        window.open(gameData.url, '_blank');
      });
      this.root.addChild(this.crossPromo);

      this.crossPromoCount = 0;
      this.crossPromoScale = 0.7;
      let iconSize: number = 256;

      let outline: PIXI.Graphics = new PIXI.Graphics();
      outline.lineStyle(6, 0xffffff, 1);
      outline.drawRect(-iconSize/2, -iconSize/2, iconSize, iconSize);
      this.crossPromo.addChild(outline);

      this.gameBoardView.getInputCatcher().signals.onPointerDown.addOnce(() => {
        this.crossPromo.visible = false;
      });

      this.resizeCrossPromo();
    }
  }

  private async loadScore(): Promise<void> {
    highscore = (await loadUserData(['highscore'])).highscore || 0;
  }

  private async updateScore(newScore: number): Promise<void> {
    if (newScore > highscore) {
      highscore = newScore;
      // window._azFeedback.Wrapper.setLeaderboardScore('bShooterChallengeSG', highscore, () => {});
      saveUserData({highscore});
    }
  }

  private async gameOver(dummyScore?: number) {
    const score = dummyScore || this.gameBoard.getData().state.score;
    const tweenMgr = this.game.tweens;
    this.gameBoardView.isPaused = true;
    this.gameBoardView.disableButtons();
    triggerGameOver(score);

    tweenMgr.create(this.gameBoardView.getRoot())
      .to({alpha: 0.2}, 300, tweenMgr.easing.Sinusoidal.Out, true);

    new GameOverPopup({
      tweenMgr,
      parent: this.root,
      restart: this.restartGame.bind(this),
      score,
      highscore: highscore || 0,
    });

    this.updateScore(score);
  }

  private async restartGame() {
    logEvent('game_retry');
    const tweenMgr = this.game.tweens;
    const waitingScreen = new WaitingScreen(this.root);
    waitingScreen.destroy();

    tweenMgr.create(this.gameBoardView.getRoot())
      .to({alpha: 0}, 400, tweenMgr.easing.Sinusoidal.Out, true);
    this.game.scenes.startScene(ScenesName.Main);
  }

  update(elapsedMs: number) {
    if (!this.gameStarted && this.gameBoard.getData().state.firstShoot) {
      this.gameStarted = true;
      triggerGameStart();
    }

    if (this.autoUpdate) {
      this.gameBoard.update(elapsedMs);
      this.gameBoardView.update(elapsedMs);
    }
    this.controller.update();

    if (this.crossPromo && this.crossPromo.visible === true) {
      this.crossPromoCount += 0.05;
      this.crossPromo.scale.set(this.crossPromoScale + ((this.crossPromoScale * 1.1) - this.crossPromoScale) * (0.5 + (0.5 * Math.cos(this.crossPromoCount))));
    }
  }

  handleResize([width, height]: [number, number]): void {
    this.bg.handleResize([width, height]);

    const diff = Math.floor(height - appHeight) / 32;
    this.level.shooter.positionY = levelData.shooter.positionY + diff;
    this.level.popOutSpace.pointZonesY = levelData.popOutSpace.pointZonesY + diff;
    this.level.popOutSpace.bouncers.y = levelData.popOutSpace.bouncers.y + diff;
    this.level.grid.deathLineY = levelData.grid.deathLineY + diff;

    this.level.grid.hexLimits.height = levelData.grid.hexLimits.height + Math.floor(diff / 1.5);

    this.gameBoard.resize(this.level);
    this.gameBoardView.resize();

    this.currHeight = height;
    this.resizeCrossPromo();
  }

  resizeCrossPromo(): void {
    if (this.crossPromo && this.crossPromo.visible === true) {
      this.crossPromo.x = 150;
      this.crossPromo.y = this.currHeight - 150;
    }
  }
}
